import React, { useState, useEffect } from "react";
import { useAction, useQuery } from "wasp/client/operations";
import { createCommunityPost, getMyProfile } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../../components/layout";
import { getRequestRecommendations } from "../requestRecommendations";
import type { CommunitySectionConfig } from "../types";
import { Phone, Mail, Save, AlertCircle } from "lucide-react";

function getListPath(
  section: CommunitySectionConfig,
  locationCode?: string | null,
) {
  const base = `/${section.path}`;
  return locationCode ? `/locations/${locationCode}${base}` : base;
}

export interface CommunityAddPageProps {
  section: CommunitySectionConfig;
}

export default function CommunityAddPage({ section }: CommunityAddPageProps) {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const createFn = useAction(createCommunityPost);

  const { data: profile } = useQuery(getMyProfile);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  useEffect(() => {
    if (profile) {
      setContactPhone((prev) => prev || profile.phoneNumber || "");
      setContactEmail((prev) => prev || profile.email || "");
    }
  }, [profile]);

  const listPath = getListPath(section, selectedLocation?.locationCode);
  const addPath = `${listPath}/add`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setErrors((prev) => ({ ...prev, title: "Title is required" }));
      return;
    }
    if (!selectedLocation?.id) {
      setErrors((prev) => ({ ...prev, location: "No location selected" }));
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    try {
      const payload: any = {
        contentType: section.contentType,
        title: title.trim(),
        description: description.trim() || undefined,
        locationId: selectedLocation.id,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      };
      if (section.expiryHours) {
        const d = new Date();
        d.setHours(d.getHours() + section.expiryHours);
        payload.expiryDate = d;
      }
      await createFn(payload);
      navigate(listPath);
    } catch (err) {
      console.error(err);
      setErrors((prev) => ({
        ...prev,
        submit: "Failed to create post. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => navigate(listPath);
  const Icon = section.icon;

  const recommendations = getRequestRecommendations(section.path);
  const applyRecommendation = (label: string, text: string) => {
    setTitle(label);
    setDescription(text);
    if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to post.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!selectedLocation) {
    return (
      <IndividualEditPageLayout title="No Location Selected" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Location Selected
          </h3>
          <p className="text-gray-600">Please select a location first.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="community-add-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Posting..." : "Post"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleCancel}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      {section.contentType === "URGENT_HELP" && (
        <SidebarSection title="Emergency Note">
          <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex flex-col gap-2 text-rose-700">
            <div className="flex items-center gap-2 font-bold text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Emergency Note
            </div>
            <p className="text-xs leading-relaxed">
              For real emergencies, please call 911 immediately. This section is
              for community assistance only.
            </p>
          </div>
        </SidebarSection>
      )}

      {section.expiryHours && (
        <SidebarSection title="Post Expiry" hideOnMobile>
          <p className="text-sm text-slate-500">
            This post will automatically expire after {section.expiryHours}{" "}
            hours.
          </p>
        </SidebarSection>
      )}

      {recommendations && (
        <SidebarSection title="Common requests" hideOnMobile>
          <div className="flex flex-wrap gap-2">
            {recommendations.items.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => applyRecommendation(item.label, item.text)}
                className="px-3 py-1.5 text-xs rounded-full bg-slate-50 hover:bg-blue-50 hover:text-blue-600 border border-slate-200 hover:border-blue-200 transition-all font-medium"
              >
                {item.label}
              </button>
            ))}
          </div>
        </SidebarSection>
      )}
    </>
  );

  return (
    <IndividualEditPageLayout
      title={`New ${section.label} Post`}
      subtitle={`Location: ${selectedLocation?.name || ""}`}
      icon={Icon}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="community-add-form" onSubmit={handleSubmit} className="space-y-8">
        <div>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title)
                setErrors((prev) => ({ ...prev, title: "" }));
            }}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent ${errors.title
              ? "border-destructive bg-destructive/10"
              : "border-input"
              }`}
            placeholder="Brief title for your post"
            required
          />
          {errors.title && (
            <p className="mt-1 text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.title}
            </p>
          )}
        </div>

        <div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent"
            placeholder="Provide details so neighbors can help or respond."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring"
              placeholder="Phone (optional)"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring"
              placeholder="Email (optional)"
            />
          </div>
        </div>
      </form>
    </IndividualEditPageLayout>
  );
}
